import { gsap } from 'gsap';
import { runWhenPageReady } from '../utils/PageLifecycle';

/**
 * Hero Gallery Animation
 * Simplified animation for homepage hero with 4 images
 * 
 * Design Principles:
 * - Y movement and opacity fades only (no scaling)
 * - Images translate from Y+100px to Y0 with softer ease (power2.out)
 * - Images appear with ~75ms stagger (first at 75ms, second at 150ms, etc.)
 * - Text appears after images with longer delay
 * - Optional: Synchronize text words with image stagger (4 words, 4 images)
 */
class HeroGalleryAnimation {
  constructor(element) {
    this.element = element;
    this.images = Array.from(element.querySelectorAll('.hero-image img'));
    this.heading = element.querySelector('.hero-heading');
    this.body = element.querySelector('.hero-body');
    this.cta = element.querySelector('.hero-cta');
    
    // Animation timing
    this.imageStagger = 0.075; // 75ms between images
    this.imageDuration = 1.2; // Duration for each image animation
    this.textDelay = 0.5; // Extra delay for text after images start
    this.textDuration = 1.0; // Duration for text animations
    
    if (this.images.length > 0) {
      this.init();
    }
  }

  init() {
    this.animateImages();
    this.animateText();
  }

  animateImages() {
    // Set initial state: hidden below
    gsap.set(this.images, {
      y: 100,
      opacity: 0
    });

    // Animate each image up and fade in with stagger
    this.images.forEach((img, index) => {
      gsap.to(img, {
        y: 0,
        opacity: 1,
        duration: this.imageDuration,
        ease: 'power2.out',
        delay: this.imageStagger * (index + 1) // First image at 75ms, second at 150ms, etc.
      });
    });
  }

  animateText() {
    // Calculate when the last image starts animating
    const lastImageStartTime = this.imageStagger * this.images.length;
    // Text starts after images have begun, with extra delay for images to "shine first"
    const textStartDelay = lastImageStartTime + this.textDelay;

    // Animate heading (if it exists and uses data-split-text-reveal)
    if (this.heading) {
      // Check if heading uses split text animation
      if (this.heading.hasAttribute('data-split-text-reveal')) {
        // Split text animation will handle this automatically
        // Just add a delay attribute to coordinate with images
        this.heading.setAttribute('data-split-delay', textStartDelay.toString());
      } else {
        // Simple fade in for heading without split text
        gsap.set(this.heading, {
          y: 40,
          opacity: 0
        });
        
        gsap.to(this.heading, {
          y: 0,
          opacity: 1,
          duration: this.textDuration,
          ease: 'power2.out',
          delay: textStartDelay
        });
      }
    }

    // Animate body text
    if (this.body) {
      gsap.set(this.body, {
        y: 40,
        opacity: 0
      });
      
      gsap.to(this.body, {
        y: 0,
        opacity: 1,
        duration: this.textDuration,
        ease: 'power2.out',
        delay: textStartDelay + 0.15 // Slightly after heading
      });
    }

    // Animate CTA button
    if (this.cta) {
      gsap.set(this.cta, {
        y: 40,
        opacity: 0
      });
      
      gsap.to(this.cta, {
        y: 0,
        opacity: 1,
        duration: this.textDuration,
        ease: 'power2.out',
        delay: textStartDelay + 0.3 // After body text
      });
    }
  }
}

// Auto-initialize on DOM ready
function initHeroGalleryAnimation() {
  const heroGallery = document.querySelector('.hero-section--gallery');
  if (heroGallery) {
    new HeroGalleryAnimation(heroGallery);
  }
}

runWhenPageReady(initHeroGalleryAnimation);

export default HeroGalleryAnimation;
